/**
 * Store config key `isOnlineOrderActive` is the eat-in / takeaway online-order
 * switch. The order-page store list hides a store when it is off. Delivery
 * keeps its own list. Table service is not closed by this helper.
 */
export var ONLINE_ORDER_SALES_TYPES = ["EATIN", "TAKEAWAY"];
export var TABLE_SERVICE_SALES_TYPES = ["CASUALDINING", "FASTCASUALDINING"];
export var DELIVERY_SALES_MODES = ["GLOVO", "DELIVEROO"];
export var ONLINE_ORDER_CLOSED_PROMPT_IDS = {
  EATIN: {
    title: "menu.preorder.closed.eatin.title",
    text: "menu.preorder.closed.text"
  },
  TAKEAWAY: {
    title: "menu.preorder.closed.takeaway.title",
    text: "menu.preorder.closed.text"
  }
};
export var ONLINE_ORDER_SHUTDOWN_ALERT_IDS = {
  EATIN: {
    title: "order.preorder.shutdown.eatin.title",
    text: "order.preorder.shutdown.text"
  },
  TAKEAWAY: {
    title: "order.preorder.shutdown.takeaway.title",
    text: "order.preorder.shutdown.text"
  }
};
export var isEatInOrTakeawaySalesType = salesType => ONLINE_ORDER_SALES_TYPES.includes(salesType);
export var isTableServiceSalesType = salesType => TABLE_SERVICE_SALES_TYPES.includes(salesType);
export var isDeliverySalesMode = selectedMode => DELIVERY_SALES_MODES.includes(selectedMode);

/**
 * Direct-link store bind. Eat-in and takeaway still resolve when the switch
 * is off so the menu can open browse-only. Table service always resolves:
 * `isOnlineOrderActive` does not close a table QR. Other sales types stay
 * on the online-order list.
 */
export function shouldResolveDirectLinkStore(store, salesType) {
  if (!store) return false;
  if (isEatInOrTakeawaySalesType(salesType) || isTableServiceSalesType(salesType)) return true;
  return isOnlineOrderStore(store);
}
export function readStoreConfigValue(config, key) {
  if (!Array.isArray(config)) return undefined;
  var entry = config.find(item => item && item.key === key);
  if (!entry || entry.value == null || entry.value === "") return undefined;
  var value = entry.value;
  if (typeof value === "boolean" || typeof value === "number") return value;
  if (typeof value !== "string") return undefined;
  if (value === "true") return true;
  if (value === "false") return false;
  if (!isNaN(value) && value.trim() !== "") return Number(value);
  return value;
}

/** Same truthiness the store list uses after config values are cast. */
export function isTruthyStoreFlag(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

/**
 * Reads `isOnlineOrderActive` from an enriched store or from a raw stores
 * query record (`config[{ key, value }]`). A loaded store with no explicit
 * true is off the list.
 */
export function isOnlineOrderActiveOnRecord(record) {
  if (!record || typeof record !== "object") return false;
  if (Array.isArray(record.config)) {
    var fromConfig = readStoreConfigValue(record.config, "isOnlineOrderActive");
    if (fromConfig !== undefined) return isTruthyStoreFlag(fromConfig);
  }
  if (Object.prototype.hasOwnProperty.call(record, "isOnlineOrderActive")) {
    return isTruthyStoreFlag(record.isOnlineOrderActive);
  }
  return false;
}
export function isOnlineOrderStore(store) {
  return isOnlineOrderActiveOnRecord(store);
}

/** A missing takeaway restriction preserves existing store behavior. */
export function isTakeawayBlockedOnRecord(record) {
  if (!record) return false;
  var configured = readStoreConfigValue(record.config, "blockTakeaway");
  return isTruthyStoreFlag(configured !== undefined ? configured : record.blockTakeaway);
}

/**
 * Eat-in and takeaway store list. Delivery keeps the existing
 * `isPreorderActive` list so Glovo and Deliveroo are unchanged.
 */
export function storesForSalesMode(stores, selectedMode) {
  var list = Array.isArray(stores) ? stores : [];
  if (isDeliverySalesMode(selectedMode)) {
    return list.filter(store => !!(store !== null && store !== void 0 && store.isPreorderActive));
  }
  return list.filter(store => isOnlineOrderStore(store) && !isOnlineOrderingClosed(store, selectedMode));
}
export function isOnlineOrderingClosed(store, salesType) {
  if (!isEatInOrTakeawaySalesType(salesType) || !store) return false;
  return !isOnlineOrderActiveOnRecord(store) || salesType === "TAKEAWAY" && isTakeawayBlockedOnRecord(store);
}

/**
 * Browse-only gate already used by `cpt=static` and `store.blockOrder`.
 * Direct eat-in/takeaway links join that same gate when `isOnlineOrderActive` is off.
 */
export function isMenuOrderingBlocked() {
  var {
    cpt,
    store,
    salesType
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return cpt === "static" || !!(store !== null && store !== void 0 && store.blockOrder) || isOnlineOrderingClosed(store, salesType);
}
export function onlineOrderClosedPromptIds(salesType) {
  return ONLINE_ORDER_CLOSED_PROMPT_IDS[salesType] || null;
}
export function onlineOrderShutdownAlertIds(salesType) {
  return ONLINE_ORDER_SHUTDOWN_ALERT_IDS[salesType] || null;
}
export function findStoreRecord(stores) {
  var {
    cps,
    lsStoreId
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  if (!Array.isArray(stores)) return null;
  return stores.find(store => {
    if (!store) return false;
    // The selected URL identity wins over a previous cart's store ID.
    if (cps) {
      if (store.cps === cps) return true;
      return Array.isArray(store.config) && store.config.some(item => item && item.key === "cps" && item.value === cps);
    }
    return !!lsStoreId && (store.ls_store_id === lsStoreId || store.id === lsStoreId);
  }) || null;
}

/** Only explicit takeaway promotions are hidden; generic Order links remain. */
export function isTakeawayLinkUnavailable(href, stores, baseUrl) {
  var allowedHosts = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : [];
  if (!href || !Array.isArray(stores) || !stores.length) return false;
  var target;
  try {
    target = new URL(href, baseUrl);
  } catch (error) {
    return false;
  }
  var base = new URL(baseUrl);
  if (target.host !== base.host && !allowedHosts.includes(target.host)) return false;
  if (target.searchParams.get("sales_type") !== "TAKEAWAY") return false;
  var cps = target.searchParams.get("cps");
  if (cps) {
    var store = findStoreRecord(stores, {
      cps
    });
    return !!store && isOnlineOrderingClosed(store, "TAKEAWAY");
  }
  return storesForSalesMode(stores, "TAKEAWAY").length === 0;
}

/**
 * A fresh store list wins over cart snapshots. Callers can require a fresh,
 * identifiable record before starting a takeaway payment/order. Delivery and
 * table service stay outside this gate.
 */
export function resolveOnlineOrderingClosed() {
  var {
    salesType,
    localStore,
    freshStores,
    cps,
    lsStoreId,
    requireFresh = false
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  if (!isEatInOrTakeawaySalesType(salesType)) return false;
  if (Array.isArray(freshStores) && freshStores.length) {
    var record = findStoreRecord(freshStores, {
      cps,
      lsStoreId
    });
    if (record) return isOnlineOrderingClosed(record, salesType);
  }
  if (requireFresh) return true;
  return isOnlineOrderingClosed(localStore, salesType);
}